import { classifySubmission, type SubmissionRecord } from './submissionReviewState';

export type LastMissionCandidate = SubmissionRecord & { deletedAt: Date | string | null };

export type LastMissionRow = LastMissionCandidate & {
  uuid: string | null;
  createdAt: Date | string | null;
  fanMissionID: bigint | number | string;
};

// gloss
export const isLastMissionEligible = (row: LastMissionCandidate): boolean =>
  row.deletedAt == null && classifySubmission(row) !== 'draft';

const createdAtMs = (value: Date | string | null): number =>
  value == null ? Number.NEGATIVE_INFINITY : new Date(value).getTime();

// gloss
export const compareNewestFirst = (a: LastMissionRow, b: LastMissionRow): number => {
  // why: two NULL createdAt values must compare as a tie, not as -Infinity minus -Infinity (NaN) - NaN makes
  // why: Array.sort treat the rows as equal and skip the fanMissionID tiebreak
  const byCreatedAt =
    a.createdAt == null && b.createdAt == null ? 0 : createdAtMs(b.createdAt) - createdAtMs(a.createdAt);
  if (byCreatedAt !== 0) return byCreatedAt;
  const aId = BigInt(a.fanMissionID);
  const bId = BigInt(b.fanMissionID);
  return aId === bId ? 0 : bId > aId ? 1 : -1;
};

// gloss
export const pickLastFanMission = <T extends LastMissionRow>(rows: readonly T[]): T | undefined =>
  [...rows].sort(compareNewestFirst).find((row) => row.uuid != null && isLastMissionEligible(row));

export type FanLastMissionRow = LastMissionRow & { fanUserUuid: string };

export type LastMissionPointer = { uuid: string; lastFanMissionUuid: string | null };

export type LastMissionRepair = { fanUserUuid: string; from: string | null; to: string | null };

export type LastMissionRepairPlan = { repairs: LastMissionRepair[]; alreadyCorrect: number };

// gloss
export const planLastFanMissionRepairs = (
  fans: readonly LastMissionPointer[],
  candidates: readonly FanLastMissionRow[],
): LastMissionRepairPlan => {
  const candidatesByFan = new Map<string, FanLastMissionRow[]>();
  for (const candidate of candidates) {
    const bucket = candidatesByFan.get(candidate.fanUserUuid);
    if (bucket) bucket.push(candidate);
    else candidatesByFan.set(candidate.fanUserUuid, [candidate]);
  }

  const plan: LastMissionRepairPlan = { repairs: [], alreadyCorrect: 0 };
  for (const fan of fans) {
    const expected = pickLastFanMission(candidatesByFan.get(fan.uuid) ?? [])?.uuid ?? null;
    if (expected === fan.lastFanMissionUuid) plan.alreadyCorrect += 1;
    else plan.repairs.push({ fanUserUuid: fan.uuid, from: fan.lastFanMissionUuid, to: expected });
  }
  return plan;
};
